import { Injectable, signal, PLATFORM_ID, inject, OnDestroy } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Observable, interval, BehaviorSubject, of } from 'rxjs';
import { tap, catchError, switchMap } from 'rxjs/operators';
import { API_CONFIG } from '../config/api.config';

interface GuestSessionData {
  session_id: string;
  expires_at: number;
  remaining_seconds?: number;
  created_at: number;
  updated_at: number;
}

// Ambos modos retornan: { success, message, data: { session_id, expires_at, ... } }
interface GuestSessionResponse {
  success: boolean;
  message: string | null;
  data?: GuestSessionData;
  timestamp?: string;
  expired?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class GuestSession implements OnDestroy {
  private sessionId = signal<string | null>(null);
  private sessionActive = signal<boolean>(false);
  private verificationInterval: any;
  private platformId = inject(PLATFORM_ID);
  private isBrowser: boolean;
  private isDestroyed = false;

  constructor(private http: HttpClient) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    if (this.isBrowser) {
      this.loadSessionFromStorage();
    }
  }

  /**
   * Carga la sesión desde localStorage si existe
   * Solo en modo manual (cookie_http_only: false)
   * En modo cookie, el session ID se maneja automáticamente por el navegador
   */
  private loadSessionFromStorage(): void {
    if (!this.isBrowser) return;
    
    // En modo cookie, NO cargar sessionId de localStorage
    // El backend maneja la sesión automáticamente vía cookies HTTP-only
    if (API_CONFIG.cookie_http_only) {
      console.log('🍪 Modo cookie_http_only activado - Session ID manejado por cookies');
      return;
    }
    
    const storedSessionId = localStorage.getItem(API_CONFIG.storage.guestSessionKey);
    if (storedSessionId) {
      this.sessionId.set(storedSessionId);
      this.sessionActive.set(true);
      
      // Verificar la sesión
      this.verifySession().subscribe({
        next: () => {
          this.sessionActive.set(true);
        },
        error: (err) => {
          console.debug('Session verification on load failed:', err);
        }
      });
    }
  }

  /**
   * Crea una nueva sesión de invitado
   * 
   * ADVERTENCIA: Si ya existe una sesión activa, este método la SOBRESCRIBIRÁ
   * y se perderá acceso al carrito de la sesión anterior.
   * 
   * Usar este método solo cuando:
   * - No hay sesión existente (primer acceso)
   * - El usuario explícitamente decidió comenzar una nueva compra
   * 
   * Si necesitas reemplazar una sesión expirada, considera usar replaceWithNewSession()
   * que documenta más claramente la intención.
   * 
   * @param expirationMinutes Minutos hasta expiración (default: config.defaultExpirationMinutes)
   * @returns Observable con la respuesta de creación de sesión
   */
  createSession(expirationMinutes?: number): Observable<GuestSessionResponse> {
    const body = {
      expiration_minutes: expirationMinutes || API_CONFIG.guestSession.defaultExpirationMinutes
    };

    // Seleccionar endpoint según modo
    const endpoint = API_CONFIG.cookie_http_only 
      ? API_CONFIG.endpoints.guestSessions.createCookie
      : API_CONFIG.endpoints.guestSessions.create;

    return this.http.post<GuestSessionResponse>(
      `${API_CONFIG.baseUrl}${endpoint}`,
      body,
      {
        headers: {
          'x-api-key': API_CONFIG.api_key
        },
        // En modo cookie, enviar credenciales para que el navegador maneje las cookies
        withCredentials: API_CONFIG.cookie_http_only
      }
    ).pipe(
      tap(response => {
        if (response.success) {
          if (API_CONFIG.cookie_http_only) {
            this.sessionActive.set(true);
            this.startVerificationInterval();
          } else if (response.data?.session_id) {
            this.sessionId.set(response.data.session_id);
            this.sessionActive.set(true);
            if (this.isBrowser) {
              localStorage.setItem(API_CONFIG.storage.guestSessionKey, response.data.session_id);
            }
            this.startVerificationInterval();
          }
        }
      }),
      catchError(error => {
        // El backend puede retornar 200 con body vacío (solo establece la cookie HTTP-only).
        // Angular falla al parsear JSON vacío → status 200 pero ok: false.
        // Tratarlo como sesión establecida correctamente.
        if (error.status === 200) {
          if (API_CONFIG.cookie_http_only) {
            this.sessionActive.set(true);
          }
          return of({ success: true, message: 'Session created' } as GuestSessionResponse);
        }
        console.error('❌ Error al crear sesión:', error);
        throw error;
      })
    );
  }

  /**
   * Verifica y extiende la sesión actual
   */
  verifySession(extensionMinutes?: number): Observable<GuestSessionResponse> {
    // En SSR, no hacer peticiones HTTP que puedan causar problemas
    if (!this.isBrowser) {
      return new Observable(observer => {
        observer.error(new Error('Cannot verify session in SSR context'));
      });
    }

    const body = {
      extension_minutes: extensionMinutes || API_CONFIG.guestSession.extensionMinutes
    };

    let url: string;
    
    // Modo cookie: usar endpoint sin session_id (lee cookie automáticamente)
    if (API_CONFIG.cookie_http_only) {
      url = API_CONFIG.endpoints.guestSessions.verifyCookie;
    } else {
      // Modo manual: requiere session_id en la URL
      const currentSessionId = this.sessionId();
      if (!currentSessionId) {
        throw new Error('No active guest session');
      }
      url = API_CONFIG.endpoints.guestSessions.verify.replace('{session_id}', currentSessionId);
    }

    return this.http.post<GuestSessionResponse>(
      `${API_CONFIG.baseUrl}${url}`,
      body,
      {
        headers: {
          'x-api-key': API_CONFIG.api_key
        },
        // En modo cookie, enviar credenciales para que el navegador envíe la cookie
        withCredentials: API_CONFIG.cookie_http_only
      }
    ).pipe(
      tap(response => {
        if (response.success) {
          this.sessionActive.set(true);
          console.log('🍪 Sesión verificada/extendida correctamente');
        }
      }),
      catchError(error => {
        // Si la sesión expiró o no existe (400 = no cookie presente)
        if (error.status === 400 || error.status === 404 || error.status === 410) {
          const sessionInfo = API_CONFIG.cookie_http_only ? 'cookie' : this.sessionId();
          console.warn('⚠️ Sesión expirada o no encontrada:', sessionInfo);
          this.sessionActive.set(false);
        }
        throw error;
      })
    );
  }

  /**
   * Obtiene información de la sesión actual sin extenderla
   */
  getSessionInfo(): Observable<GuestSessionResponse> {
    const currentSessionId = this.sessionId();
    if (!currentSessionId) {
      throw new Error('No active guest session');
    }

    const url = API_CONFIG.endpoints.guestSessions.get.replace('{session_id}', currentSessionId);
    return this.http.get<GuestSessionResponse>(`${API_CONFIG.baseUrl}${url}`, {
      headers: {
        'x-api-key': API_CONFIG.api_key
      }
    });
  }

  /**
   * Elimina la sesión manualmente
   */
  deleteSession(): Observable<any> {
    const currentSessionId = this.sessionId();
    if (!currentSessionId) {
      return new Observable(observer => observer.complete());
    }

    const url = API_CONFIG.endpoints.guestSessions.delete.replace('{session_id}', currentSessionId);
    return this.http.delete(`${API_CONFIG.baseUrl}${url}`, {
      headers: {
        'x-api-key': API_CONFIG.api_key
      }
    }).pipe(
      tap(() => {
        this.clearSession();
      })
    );
  }

  /**
   * Limpia la sesión local
   * En modo cookie, solo limpia estado local (la cookie se maneja por el backend)
   */
  private clearSession(): void {
    this.sessionActive.set(false);
    
    // En modo manual, limpiar sessionId y localStorage
    if (!API_CONFIG.cookie_http_only) {
      this.sessionId.set(null);
      if (this.isBrowser) {
        localStorage.removeItem(API_CONFIG.storage.guestSessionKey);
      }
    }
    
    this.stopVerificationInterval();
  }

  /**
   * Reemplaza la sesión actual con una nueva
   * Útil cuando la sesión expiró y el usuario decide comenzar una nueva compra
   * ADVERTENCIA: Esto eliminará cualquier carrito asociado a la sesión anterior
   */
  replaceWithNewSession(expirationMinutes?: number): Observable<GuestSessionResponse> {
    console.log('🔄 Reemplazando sesión actual con una nueva');
    
    // Primero limpiar la sesión actual
    this.clearSession();
    
    // Crear una nueva sesión
    return this.createSession(expirationMinutes);
  }

  /**
   * Inicia el intervalo de verificación periódica
   */
  private startVerificationInterval(): void {
    // No iniciar intervalo en SSR
    if (!this.isBrowser || this.isDestroyed) return;
    
    this.stopVerificationInterval();
    const intervalMs = API_CONFIG.guestSession.verifyIntervalMinutes * 60 * 1000;
    
    this.verificationInterval = setInterval(() => {
      if (this.isDestroyed || !this.isBrowser) {
        this.stopVerificationInterval();
        return;
      }
      this.verifySession().subscribe({
        error: (err) => {
          // Solo log en browser, no en SSR
          if (this.isBrowser) {
            console.error('Error verifying guest session:', err);
          }
        }
      });
    }, intervalMs);
  }

  /**
   * Detiene el intervalo de verificación
   */
  private stopVerificationInterval(): void {
    if (this.verificationInterval) {
      clearInterval(this.verificationInterval);
      this.verificationInterval = null;
    }
  }

  /**
   * Asegura que existe una sesión activa.
   * En modo cookie, siempre llama a createSession() que usa el endpoint
   * idempotente /sessions: verifica si hay cookie válida, si existe la extiende,
   * si no existe crea una nueva. Un solo endpoint para ambos casos.
   */
  ensureSession(): Observable<GuestSessionResponse> {
    // SSR: no intentar crear/verificar sesión en el servidor
    if (!this.isBrowser) {
      return of({ success: true, message: 'SSR mode - session skipped' } as GuestSessionResponse);
    }

    if (API_CONFIG.cookie_http_only) {
      // En modo cookie, el backend puede limpiar _te_gid (ej. al login).
      // Verificar primero y, si no existe cookie válida, crear una nueva sesión.
      return this.verifySession().pipe(
        catchError(() => this.createSession())
      );
    }
    // Modo manual: verificar si ya hay sesión activa en memoria
    const currentSessionId = this.sessionId();
    if (currentSessionId) {
      // Si hay session_id, intentar renovarla para preservar carrito existente.
      // Solo crear nueva sesión si la verificación falla (expirada/inválida).
      return this.verifySession().pipe(
        catchError(() => this.createSession())
      );
    }
    return this.createSession();
  }

  /**
   * Obtiene el ID de sesión actual
   * 
   * COMPORTAMIENTO SEGÚN MODO:
   * 
   * MODO COOKIE (cookie_http_only: true):
   * - Retorna NULL siempre
   * - El session ID se maneja automáticamente en cookies HTTP-only
   * - El navegador envía la cookie en cada petición
   * - El frontend NO debe ni puede acceder al session ID
   * 
   * MODO MANUAL (cookie_http_only: false):
   * - Retorna el session ID almacenado localmente
   * - Mantiene el session ID incluso si expiró (para referencia al carrito)
   * - Flujo de recuperación: signal → localStorage → null
   * 
   * @returns El session ID actual (modo manual) o null (modo cookie)
   */
  getSessionId(): string | null {
    // En modo cookie, SIEMPRE retornar null
    // El session ID se maneja automáticamente por cookies HTTP-only
    if (API_CONFIG.cookie_http_only) {
      return null;
    }
    
    // Modo manual: intentar obtener del signal (memoria)
    const sessionFromSignal = this.sessionId();
    if (sessionFromSignal) {
      return sessionFromSignal;
    }
    
    // Fallback a localStorage
    if (this.isBrowser) {
      const storedSessionId = localStorage.getItem(API_CONFIG.storage.guestSessionKey);
      
      // Si hay session ID en localStorage pero no en signal, sincronizar
      if (storedSessionId) {
        console.log('🔄 Sincronizando session ID desde localStorage al signal');
        this.sessionId.set(storedSessionId);
        this.sessionActive.set(true);
        return storedSessionId;
      }
    }
    
    return null;
  }

  /**
   * Verifica si hay una sesión activa
   */
  isSessionActive(): boolean {
    return this.sessionActive();
  }

  /**
   * Valida que haya un session ID disponible
   * Sin crear uno nuevo - preserva el carrito existente
   * 
   * NOTA: En modo cookie_http_only, siempre retorna true si la sesión está activa
   * porque el session ID está en la cookie HTTP-only y no es accesible desde JS
   * 
   * @returns true si hay session ID disponible o sesión activa (modo cookie), false si no
   */
  hasSessionId(): boolean {
    // En modo cookie, verificar solo si la sesión está marcada como activa
    if (API_CONFIG.cookie_http_only) {
      return this.sessionActive();
    }
    
    // Modo manual: verificar que exista session ID
    const sessionId = this.getSessionId();
    return sessionId !== null && sessionId !== undefined && sessionId.length > 0;
  }

  /**
   * Limpia los recursos cuando el servicio se destruye
   */
  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.stopVerificationInterval();
  }
}
